import { create } from 'zustand';
import { ApiOperatorTaskView } from '@/core/generated/api';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { isCancel } from 'axios';
import { InlineApiError } from '@/core/api/requestErrorMode';
import { operatorTaskService } from '../api/operatorTaskService';
import { filtersToTaskQuery } from '../mappers';
import type { OperatorTaskState, OperatorTaskStore } from './types';

const initial: OperatorTaskState = {
    list: null,
    listStatus: RequestStatus.Idle,
    listError: null,
    listRevision: 0,
    current: null,
    detailsStatus: RequestStatus.Idle,
    detailsError: null,
    detailsRevision: 0,
    history: [],
    historyCount: 0,
    historyStatus: RequestStatus.Idle,
    historyError: null,
    historyRevision: 0,
    summary: null,
    summaryStatus: RequestStatus.Idle,
    summaryError: null,
    summaryRevision: 0,
    summarySuccessRevision: 0,
    clockOffset: 0,
    summaryUpdatedAt: null,
    summaryHttpStatus: null,
    notificationsOpen: false,
    notificationsView: 'attention',
    notificationsPage: 0,
    notificationsPageSize: 20,
    attention: null,
    attentionStatus: RequestStatus.Idle,
    attentionError: null,
    attentionRevision: 0,
    attentionUpdatedAt: null,
    reminderFeed: null,
    feedStatus: RequestStatus.Idle,
    feedError: null,
    feedRevision: 0,
    feedUpdatedAt: null,
    notificationRefreshRevision: 0,
    options: null,
    optionsStatus: RequestStatus.Idle,
    optionsError: null,
    optionsRevision: 0,
    filters: {
        view: ApiOperatorTaskView.InProgress,
        search: '',
        type: null,
        date: null,
        ordering: 'priority',
        first: 0,
        take: 20,
        orderId: null,
        chatId: null,
        contextKind: 'all',
        related: null,
        assignee: null
    },
};

/** Хранит общую очередь задач и сводку смены */
export const useOperatorTasks = create<OperatorTaskStore>((set, get) => ({
    ...initial,

    setFilters: (filters) => set(() => {
        const next = {
            ...get().filters,
            first: 0,
            ...filters,
        };

        if ('tableFilters' in filters) next.date = null;

        if ('date' in filters && !('tableFilters' in filters)) {
            const { dueAtUtc: _previousDue, ...remaining } = next.tableFilters ?? {};
            next.tableFilters = remaining;
        }

        return { filters: next };
    }),

    fetchList: async (inline = false) => {
        const revision = get().listRevision + 1;

        set({
            listRevision: revision,
            listStatus: RequestStatus.Loading,
            listError: null
        });

        try {
            const list = await operatorTaskService.list(filtersToTaskQuery(get().filters), inline);

            if (revision !== get().listRevision) return;

            set({
                list,
                listStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (revision !== get().listRevision) return;

            set({
                listStatus: RequestStatus.Error,
                listError: getErrorMessage(error, 'Не удалось загрузить задачи')
            });
        }
    },

    fetchDetails: async (id) => {
        const revision = get().detailsRevision + 1;

        set({
            current: null,
            detailsRevision: revision,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const current = await operatorTaskService.details(id);

            if (revision !== get().detailsRevision) return;

            set({
                current,
                detailsStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (revision !== get().detailsRevision) return;

            set({
                detailsStatus: RequestStatus.Error,
                detailsError: getErrorMessage(error, 'Не удалось открыть задачу')
            });
        }
    },

    fetchHistory: async (id, append = false) => {
        const revision = get().historyRevision + 1;

        const previous = append
            ? get().history
            : [];

        set({
            history: previous,
            historyRevision: revision,
            historyStatus: RequestStatus.Loading,
            historyError: null
        });

        try {
            const page = await operatorTaskService.history(id, previous.length, Math.min(20, get().options?.listMaxTake ?? 200));

            if (revision !== get().historyRevision) return;

            set({
                history: [...previous, ...(page.items ?? [])],
                historyCount: page.allCount ?? 0,
                historyStatus: RequestStatus.Success
            });
        } catch (error: unknown) {
            if (revision !== get().historyRevision) return;

            set({
                historyStatus: RequestStatus.Error,
                historyError: getErrorMessage(error, 'Не удалось загрузить историю')
            });
        }
    },

    fetchSummary: async (force = false, inline = false, signal) => {
        if (!force && get().summaryStatus === RequestStatus.Loading) return;

        const revision = get().summaryRevision + 1;

        set({
            summaryRevision: revision,
            summaryStatus: RequestStatus.Loading,
            summaryError: null
        });

        try {
            const summary = await operatorTaskService.summary(inline, signal);

            if (revision !== get().summaryRevision) return;

            const receivedAt = Date.now();
            const clockOffset = Date.parse(summary.serverTimeUtc ?? '') - receivedAt || 0;

            set({
                summary,
                summaryStatus: RequestStatus.Success,
                summarySuccessRevision: get().summarySuccessRevision + 1,
                summaryHttpStatus: null,
                summaryUpdatedAt: new Date(receivedAt + clockOffset).toISOString(),
                clockOffset,
            });
        } catch (error: unknown) {
            if (revision !== get().summaryRevision) return;
            if (isCancel(error)) return;

            set({
                summaryStatus: RequestStatus.Error,
                summaryHttpStatus: error instanceof InlineApiError
                    ? error.status ?? null
                    : null,
                summaryError: getErrorMessage(error, 'Сводка задач недоступна'),
            });
        }
    },

    openNotifications: () => set({
        notificationsOpen: true,
        notificationsPage: 0
    }),

    closeNotifications: () => set({ notificationsOpen: false }),

    setNotificationsView: (view) => set({
        notificationsView: view,
        notificationsPage: 0
    }),

    setNotificationsPage: (page) => set({ notificationsPage: page }),

    setNotificationsPageSize: (size) => set({
        notificationsPageSize: Math.max(1, Math.min(size, get().options?.listMaxTake ?? 200)),
        notificationsPage: 0,
        attentionRevision: get().attentionRevision + 1,
        feedRevision: get().feedRevision + 1,
    }),

    invalidateNotifications: () => set({ notificationRefreshRevision: get().notificationRefreshRevision + 1 }),

    fetchNotifications: async (signal) => {
        const state = get();
        const takeCount = Math.min(state.notificationsPageSize, state.options?.listMaxTake ?? 200);

        const query = {
            takeCount,
            skipCount: state.notificationsPage * takeCount
        };

        if (state.notificationsView === 'attention') {
            const revision = state.attentionRevision + 1;

            set({
                attentionRevision: revision,
                attentionStatus: RequestStatus.Loading,
                attentionError: null
            });

            try {
                const attention = await operatorTaskService.attention(query, signal);

                const attentionRequestStale = revision !== get().attentionRevision || get().notificationsView !== state.notificationsView || get().notificationsPage !== state.notificationsPage;

                if (attentionRequestStale) return;

                if (state.notificationsPage > 0 && query.skipCount >= (attention.allCount ?? 0)) {
                    set({ notificationsPage: 0 });
                    get().invalidateNotifications();

                    return;
                }

                set({
                    attention,
                    attentionStatus: RequestStatus.Success,
                    attentionUpdatedAt: attention.serverTimeUtc ?? new Date(Date.now() + get().clockOffset).toISOString()
                });
            } catch (error: unknown) {
                if (revision !== get().attentionRevision || isCancel(error)) return;

                set({
                    attentionStatus: RequestStatus.Error,
                    attentionError: getErrorMessage(error, 'Не удалось загрузить очередь уведомлений')
                });
            }
        } else {
            const revision = state.feedRevision + 1;

            set({
                feedRevision: revision,
                feedStatus: RequestStatus.Loading,
                feedError: null
            });

            try {
                const reminderFeed = await operatorTaskService.reminders(query, signal);

                const feedRequestStale = revision !== get().feedRevision || get().notificationsView !== state.notificationsView || get().notificationsPage !== state.notificationsPage;

                if (feedRequestStale) return;

                if (state.notificationsPage > 0 && query.skipCount >= (reminderFeed.allCount ?? 0)) {
                    set({ notificationsPage: 0 });
                    get().invalidateNotifications();

                    return;
                }

                set({
                    reminderFeed,
                    feedStatus: RequestStatus.Success,
                    feedUpdatedAt: reminderFeed.serverTimeUtc ?? new Date(Date.now() + get().clockOffset).toISOString()
                });
            } catch (error: unknown) {
                if (revision !== get().feedRevision || isCancel(error)) return;

                set({
                    feedStatus: RequestStatus.Error,
                    feedError: getErrorMessage(error, 'Не удалось загрузить ленту напоминаний')
                });
            }
        }
    },

    fetchOptions: async () => {
        if (get().optionsStatus === RequestStatus.Loading) return;

        const revision = get().optionsRevision + 1;

        set({
            optionsRevision: revision,
            optionsStatus: RequestStatus.Loading,
            optionsError: null
        });

        try {
            const options = await operatorTaskService.options();

            if (revision !== get().optionsRevision) return;

            const anchor = Date.parse(options.serverTimeUtc ?? '');

            const clockOffset = get().summarySuccessRevision === 0 && Number.isFinite(anchor)
                ? anchor - Date.now()
                : get().clockOffset;

            const state = get();

            const defaultTake = state.options
                ? state.filters.take
                : options.listDefaultTake ?? state.filters.take;

            const take = Math.min(defaultTake, options.listMaxTake ?? 200);

            set({
                options,
                optionsStatus: RequestStatus.Success,
                clockOffset,
                ...(take === state.filters.take
                    ? {}
                    : {
                        filters: {
                            ...state.filters,
                            first: 0,
                            take
                        }
                    }),
                ...(options.listMaxTake !== state.options?.listMaxTake
                    ? { notificationsPage: 0 }
                    : {}),
            });
        } catch (error: unknown) {
            if (revision !== get().optionsRevision) return;

            set({
                optionsStatus: RequestStatus.Error,
                optionsError: getErrorMessage(error, 'Не удалось загрузить настройки формы')
            });
        }
    },

    clearCurrent: () => set({
        current: null,
        detailsStatus: RequestStatus.Idle,
        detailsError: null,
        detailsRevision: get().detailsRevision + 1,
        history: [],
        historyCount: 0,
        historyStatus: RequestStatus.Idle,
        historyError: null,
        historyRevision: get().historyRevision + 1,
    }),

    reset: () => set({
        ...initial,
        listRevision: get().listRevision + 1,
        detailsRevision: get().detailsRevision + 1,
        historyRevision: get().historyRevision + 1,
        summaryRevision: get().summaryRevision + 1,
        optionsRevision: get().optionsRevision + 1,
        attentionRevision: get().attentionRevision + 1,
        feedRevision: get().feedRevision + 1,
    }),
}));
